/**
 * React Query hook for Activity Types (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { listActivityTypes } from 'services/timerServices';
import { totalCountEvent } from 'services/generalServices';
import { queryKeys } from '../config/queryClient';
import { useState, useCallback } from 'react';

/**
 * Fetches activity types data
 * Always loads the full list by getting total count first, then fetching all items
 * Formats activity types with activity_label
 *
 * @param {number} pageLimit - Limit for fetching activity types
 * @param {string} pagination_token - Pagination token (default: '')
 * @param {string} pagination_direction - Pagination direction ('forward' or 'reverse') (default: 'forward')
 * @param {string} next - Next page token (default: '')
 * @param {Object} filters - Filter object with category, status, and practice_area_ids
 * @param {boolean} excludeFromLoading - Exclude from loading state (default: false)
 * @returns {Promise<Array>} - Array of activity types with activity_label formatted
 */
const fetchActivityTypes = async (
  pageLimit,
  pagination_token = '',
  pagination_direction = 'forward',
  next = '',
  filters = {},
  excludeFromLoading = false
) => {
  try {
    const response = await listActivityTypes(next, pageLimit, pagination_token, pagination_direction, filters, excludeFromLoading);

    if (response?.data?.activity_types) {
      // Format activity types with activity_label
      const formattedTypes = response.data.activity_types.map((type) => ({
        ...type,
        activity_label: `${type.activity_type} - ${type.activity_type_code}`,
      }));

      return formattedTypes;
    }

    return [];
  } catch (error) {
    console.error('Error fetching activity types:', error);
    throw error;
  }
};

/**
 * Hook to fetch activity types with React Query
 * Always loads the full list by getting total count first
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Number of items per page (default: uses total count)
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @param {string} options.next - Next page token (default: '')
 * @param {Object} options.filters - Filter object with category, status, and practice_area_ids (default: {})
 * @param {boolean} options.excludeFromLoading - Exclude from loading state (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - activityTypeList, isActivityTypeLoading, error, refetch, isFetching, loadActivityTypes
 */
export const useActivityTypesQuery = ({
  limit,
  pagination_token = '',
  pagination_direction = 'forward',
  next = '',
  filters = {},
  excludeFromLoading = false,
  enabled = true,
} = {}) => {
  // Track practice area IDs separately to allow dynamic updates
  const [practiceAreaIds, setPracticeAreaIds] = useState(filters.practice_area_ids || null);

  // Get total count first - only if limit is not provided
  const { data: countData } = useQuery({
    queryKey: ['activity-type-count'],
    queryFn: async () => {
      const countResponse = await totalCountEvent('activity_type');
      return countResponse?.data || 10;
    },
    enabled: enabled && !limit, // Only fetch count if limit is not provided
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes (gcTime replaces cacheTime in v4+)
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  const pageLimit = limit || countData || 10;

  // Merge filters with practice area IDs
  const mergedFilters = {
    ...filters,
    ...(practiceAreaIds && practiceAreaIds.length > 0 ? { practice_area_ids: practiceAreaIds } : {}),
  };

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.activityTypes.list(pageLimit, pagination_token, pagination_direction, next, mergedFilters),
    queryFn: () => fetchActivityTypes(pageLimit, pagination_token, pagination_direction, next, mergedFilters, excludeFromLoading),
    enabled: enabled && (limit || !!countData), // Only fetch when limit is provided or count is available
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes (gcTime replaces cacheTime in v4+)
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  /**
   * Load activity types with specific practice area IDs
   * Updates the practice area IDs state, which triggers a refetch
   * @param {Array|null} newPracticeAreaIds - Array of practice area IDs or null
   */
  const loadActivityTypes = useCallback((newPracticeAreaIds = null) => {
    setPracticeAreaIds(newPracticeAreaIds);
    // The query will automatically refetch when practiceAreaIds changes
  }, []);

  return {
    activityTypeList: data || [],
    isActivityTypeLoading: isLoading,
    isFetching,
    error,
    refetch,
    loadActivityTypes,
  };
};
